"use client";
import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

type AnimatedCounterProps = {
  /** Final value to count up to. */
  value: number;
  /** Text shown before the number, e.g. "$". */
  prefix?: string;
  /** Text shown after the number, e.g. "+", "%", "M". */
  suffix?: string;
  /** Number of decimal places to format to. */
  decimals?: number;
  /** Animation duration in ms. */
  duration?: number;
  className?: string;
  /** Use locale thousands separators (1,845 vs 1845). Defaults true. */
  separator?: boolean;
};

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Counts up from 0 to `value` once it scrolls into view. Used by the live
 * metrics bar and stats section. Falls back to displaying the final value
 * immediately when the user prefers reduced motion.
 */
export default function AnimatedCounter({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 1400,
  className = "",
  separator = true,
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(value);
      return;
    }

    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      setDisplay(value * easeOutCubic(progress));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isInView, value, duration]);

  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.round(display).toString();
  const withSeparators = separator
    ? formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : formatted;

  return (
    <span ref={ref} className={className}>
      {prefix}
      {withSeparators}
      {suffix}
    </span>
  );
}
